export const CALL_LOG_PART_TYPE = "data-call" as const;

export type CallLogOutcome = "completed" | "missed" | "declined" | "cancelled";

export type CallLogData = {
  callId: string;
  callType: "audio" | "video";
  outcome: CallLogOutcome;
  initiatedByUserId: string;
  isGroup: boolean;
  durationSeconds?: number;
};

export type CallLogPart = {
  type: typeof CALL_LOG_PART_TYPE;
  data: CallLogData;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function buildCallLogPart(data: CallLogData): CallLogPart {
  return { type: CALL_LOG_PART_TYPE, data };
}

export function parseCallLogData(raw: unknown): CallLogData | null {
  if (!isRecord(raw)) return null;
  const { callId, callType, outcome, initiatedByUserId, isGroup, durationSeconds } = raw;
  if (typeof callId !== "string" || !callId) return null;
  if (callType !== "audio" && callType !== "video") return null;
  if (
    outcome !== "completed" &&
    outcome !== "missed" &&
    outcome !== "declined" &&
    outcome !== "cancelled"
  ) {
    return null;
  }
  if (typeof initiatedByUserId !== "string" || !initiatedByUserId) return null;
  if (typeof isGroup !== "boolean") return null;
  if (durationSeconds != null && (typeof durationSeconds !== "number" || durationSeconds < 0)) {
    return null;
  }
  return {
    callId,
    callType,
    outcome,
    initiatedByUserId,
    isGroup,
    ...(typeof durationSeconds === "number" ? { durationSeconds } : {}),
  };
}

export function parseCallLogPart(parts: ReadonlyArray<{ type: string; data?: unknown }>): CallLogData | null {
  const part = parts.find((p) => p.type === CALL_LOG_PART_TYPE);
  if (!part) return null;
  return parseCallLogData(part.data);
}

export function formatCallDuration(totalSeconds: number): string {
  if (totalSeconds < 60) {
    const s = Math.max(0, Math.round(totalSeconds));
    return `0:${s.toString().padStart(2, "0")}`;
  }
  const minutes = Math.max(1, Math.round(totalSeconds / 60));
  return `${minutes} min`;
}

/** Neutral preview for inbox / push (not viewer-specific). */
export function formatCallLogInboxPreview(data: CallLogData): string {
  const media = data.callType === "video" ? "Video call" : "Voice call";
  const groupPrefix = data.isGroup ? "Group " : "";

  if (data.outcome === "completed") {
    const duration =
      typeof data.durationSeconds === "number"
        ? formatCallDuration(data.durationSeconds)
        : null;
    return duration ? `${groupPrefix}${media} · ${duration}` : `${groupPrefix}${media}`;
  }

  if (data.outcome === "declined") {
    return `${groupPrefix}${media} declined`;
  }

  if (data.outcome === "cancelled") {
    return "Call cancelled";
  }

  return `Missed ${data.callType === "video" ? "video" : "voice"} call`;
}

/** Thread label; pass selfUserId for caller/callee-specific wording. */
export function formatCallLogPreview(data: CallLogData, selfUserId?: string): string {
  const media = data.callType === "video" ? "Video call" : "Voice call";
  const groupPrefix = data.isGroup ? "Group " : "";
  const initiatedBySelf = selfUserId != null && data.initiatedByUserId === selfUserId;

  if (data.outcome === "completed") {
    const duration =
      typeof data.durationSeconds === "number"
        ? formatCallDuration(data.durationSeconds)
        : null;
    return duration ? `${groupPrefix}${media} · ${duration}` : `${groupPrefix}${media}`;
  }

  if (data.outcome === "declined") {
    if (initiatedBySelf) {
      return `${groupPrefix}${media} declined`;
    }
    return `Declined ${data.callType === "video" ? "video" : "voice"} call`;
  }

  if (data.outcome === "cancelled") {
    return initiatedBySelf ? "Call cancelled" : `Missed ${data.callType === "video" ? "video" : "voice"} call`;
  }

  if (initiatedBySelf) {
    return "No answer";
  }
  return `Missed ${data.callType === "video" ? "video" : "voice"} call`;
}

export function isMissedCallLog(data: CallLogData, selfUserId: string): boolean {
  return (
    data.outcome === "missed" ||
    (data.outcome === "declined" && data.initiatedByUserId !== selfUserId) ||
    (data.outcome === "cancelled" && data.initiatedByUserId !== selfUserId)
  );
}

export function callDurationSeconds(
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
): number | undefined {
  if (!startedAt || !endedAt) return undefined;
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return undefined;
  return Math.round((end - start) / 1000);
}
